import { useEffect, useState } from 'react';
import { useCompany } from '../../context/CompanyContext';
import api from '../../api';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const EMPTY_FORM = {
  taxable_sales: '', export_exempt_sales: '', direct_expenses: '', indirect_expenses: '',
  other_income: '', cash_balance: '', receivables: '',
};

function inr(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Rounds a stored float to 2 decimals before it goes into a form field -
// Tally-sync figures can arrive as e.g. 41369.549999999996 (float summation
// noise), which otherwise shows up raw and ugly in a number input.
function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function FinancialsEntryTab() {
  const { selectedCompanyId } = useCompany();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedCompanyId) return;
    api
      .get(`/financial-data/${selectedCompanyId}`)
      .then((res) => {
        const existing = res.data.find((r) => r.month === month && r.year === year);
        setForm(
          existing
            ? {
                taxable_sales: round2(existing.taxable_sales), export_exempt_sales: round2(existing.export_exempt_sales),
                direct_expenses: round2(existing.direct_expenses), indirect_expenses: round2(existing.indirect_expenses),
                other_income: round2(existing.other_income), cash_balance: round2(existing.cash_balance),
                receivables: round2(existing.receivables),
              }
            : EMPTY_FORM
        );
        setSaved(null);
      })
      .catch((err) => setError(err.message));
  }, [selectedCompanyId, month, year]);

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const revenue = (Number(form.taxable_sales) || 0) + (Number(form.export_exempt_sales) || 0);
  const grossProfit = revenue - (Number(form.direct_expenses) || 0);
  const ebitda = grossProfit + (Number(form.other_income) || 0) - (Number(form.indirect_expenses) || 0);

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      company_id: selectedCompanyId,
      month,
      year,
      taxable_sales: Number(form.taxable_sales) || 0,
      export_exempt_sales: Number(form.export_exempt_sales) || 0,
      direct_expenses: Number(form.direct_expenses) || 0,
      indirect_expenses: Number(form.indirect_expenses) || 0,
      other_income: Number(form.other_income) || 0,
      cash_balance: Number(form.cash_balance) || 0,
      receivables: Number(form.receivables) || 0,
    };
    api
      .post('/financial-data', payload)
      .then((res) => setSaved(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false));
  };

  return (
    <form className="dash-card" onSubmit={handleSave}>
      <h2 className="dash-card-title">Revenue / Financials Data Entry</h2>

      <div className="de-period-row">
        <div className="field-group">
          <span className="field-label">Month</span>
          <select className="field-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>
        <div className="field-group">
          <span className="field-label">Year</span>
          <input className="field-input" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </div>
      </div>

      {error && <p className="dash-error">{error}</p>}

      <div className="field-row">
        <div className="field-group">
          <span className="field-label">Taxable Sales ₹</span>
          <input className="field-input" type="number" value={form.taxable_sales} onChange={setField('taxable_sales')} />
        </div>
        <div className="field-group">
          <span className="field-label">Export / Exempt Sales ₹</span>
          <input className="field-input" type="number" value={form.export_exempt_sales} onChange={setField('export_exempt_sales')} />
        </div>
        <div className="field-group">
          <span className="field-label">Total Revenue ₹ (auto)</span>
          <input className="field-input" value={inr(revenue)} readOnly />
        </div>
        <div className="field-group">
          <span className="field-label">Direct Expenses ₹</span>
          <input className="field-input" type="number" value={form.direct_expenses} onChange={setField('direct_expenses')} />
        </div>
        <div className="field-group">
          <span className="field-label">Gross Profit ₹ (auto)</span>
          <input className="field-input" value={inr(grossProfit)} readOnly />
        </div>
        <div className="field-group">
          <span className="field-label">Indirect Expenses ₹</span>
          <input className="field-input" type="number" value={form.indirect_expenses} onChange={setField('indirect_expenses')} />
        </div>
        <div className="field-group">
          <span className="field-label">Other Income ₹</span>
          <input className="field-input" type="number" value={form.other_income} onChange={setField('other_income')} />
        </div>
        <div className="field-group">
          <span className="field-label">EBITDA ₹ (auto)</span>
          <input className="field-input" value={inr(ebitda)} readOnly />
        </div>
        <div className="field-group">
          <span className="field-label">Cash in Bank ₹</span>
          <input className="field-input" type="number" value={form.cash_balance} onChange={setField('cash_balance')} />
        </div>
        <div className="field-group">
          <span className="field-label">Trade Receivables ₹</span>
          <input className="field-input" type="number" value={form.receivables} onChange={setField('receivables')} />
        </div>
      </div>

      <div className="dash-btn-row">
        <button className="dash-btn dash-btn-orange" type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Financial Data'}
        </button>
      </div>

      {saved && (
        <div className="de-summary">
          Saved — EBITDA for {MONTH_NAMES[month - 1]} {year}: {inr(saved.ebitda)}
        </div>
      )}
    </form>
  );
}

export default FinancialsEntryTab;
